import { useEffect, useRef, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { API } from './Api';
import { useRouter } from 'next/navigation';
import { User } from '@/types/User';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';

const STORAGE_KEY_USER = 'U:user';

export function getRandomString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function useSubject<T>(subject: BehaviorSubject<T>, callback: (value: T) => void) {
  useEffect(() => {
    const sub = subject.subscribe(callback);
    return () => sub.unsubscribe();
  }, [subject, callback]);
}

export function useObservable<T>(subject: BehaviorSubject<T>) {
  const [value, setValue] = useState(subject.value);
  useEffect(() => {
    const sub = subject.subscribe((item) => {
      setValue(item);
    });
    return () => {
      sub.unsubscribe();
    };
  }, [subject]);
  return value;
}

export function useGuard() {
  const [mounted, setMounted] = useState(false);
  const router = useRouter();
  useEffect(() => {
    API.get<User>(User.API + 'me/').then((res) => {
      if (res.success) {
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(res.body));
        setMounted(true);
      } else {
        router.push('/login');
      }
    });
  }, []);
  return mounted;
}

export function getUserByGuard(): User {
  const user: User = JSON.parse(localStorage.getItem(STORAGE_KEY_USER)!);
  return user;
}

const debounceTable: { [key: string]: NodeJS.Timeout } = {};
export function debounce(key: string, func: () => void, delay: number) {
  if (!(key in debounceTable)) {
    debounceTable[key] = setTimeout(func, delay);
  } else {
    clearTimeout(debounceTable[key]);
    debounceTable[key] = setTimeout(func, delay);
  }
}

export function useDebounce<T>(value: T, delay: number, callback?: (debouncedValue: T) => void): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const remember = useRef<T>(value);

  useEffect(() => {
    if (remember.current != value) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
        if (callback) {
          callback(value);
        }
      }, delay);
      remember.current = value;
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay, callback]);

  return debouncedValue;
}

export function useInit() {
  const [init, setInit] = useState(true);
  useEffect(() => {
    setInit(false);
  }, []);
  return init;
}

export function capitalize(value: string) {
  const str = value.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function media(value?: string) {
  if (value) {
    if (value.startsWith('./')) {
      return process.env.NEXT_PUBLIC_API_SERVER + value.substring(1);
    } else if (value.startsWith('/')) {
      // Handle absolute paths like /media/... by adding API server base
      return process.env.NEXT_PUBLIC_API_SERVER + value;
    } else {
      return value;
    }
  } else {
    return '/images/temp/img_profile_temp.png';
  }
}

export function getFile(accept = 'image/*'): Promise<File | undefined> {
  return new Promise<File | undefined>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';

    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      if (document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    input.onchange = async (e) => {
      const files = [...((e.target as HTMLInputElement).files || [])];
      cleanup();
      const resizedFiles = await resizeMultipleImages(files);
      resolve(resizedFiles[0]);
    };

    const onFocus = () => {
      setTimeout(() => {
        if (!input.value) {
          cleanup();
          resolve(undefined);
        }
      }, 300);
    };

    window.addEventListener('focus', onFocus, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

export function getFiles(accept = 'image/*'): Promise<File[]> {
  return new Promise<File[]>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = accept;
    input.style.display = 'none';

    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      if (document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    input.onchange = async (e) => {
      const files = [...((e.target as HTMLInputElement).files || [])];
      const resizedFiles = await resizeMultipleImages(files);
      cleanup();
      resolve(resizedFiles);
    };

    const onFocus = () => {
      setTimeout(() => {
        if (!input.value) {
          cleanup();
          resolve([]);
        }
      }, 300);
    };

    window.addEventListener('focus', onFocus, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

export function isImageFile(file: File) {
  if (!file || !file.type) {
    return false;
  }
  return file.type.startsWith('image/');
}

export function isGlbFile(file: File) {
  if (!file || !file.type) {
    return false;
  }
  const hasGlbExtension = file.name.toLowerCase().endsWith('.glb');
  const hasGlbMimeType = file.type === 'model/gltf-binary';

  // MIME 타입이 설정되지 않은 경우 확장자로 판단
  return hasGlbMimeType || (file.type === '' && hasGlbExtension);
}

export function toggleSubject<T>(subject: BehaviorSubject<T | undefined>, value: T) {
  if (subject.value === value) {
    subject.next(undefined);
  } else {
    subject.next(value);
  }
}

function getMimeTypeFromUrl(url: string) {
  const extensionMap: { [s: string]: string } = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    glb: 'model/gltf-binary',
  };
  const matches = url.match(/\.([a-z0-9]+)(?:[\?#]|$)/i);
  if (matches && matches[1]) {
    const ext = matches[1].toLowerCase();
    return extensionMap[ext] || 'image/jpeg';
  }
  return 'image/jpeg';
}

function getFileExtensionFromUrl(url: string) {
  const matches = url.match(/\.([a-z0-9]+)(?:[\?#]|$)/i);
  return matches ? matches[1] : 'jpg'; // 확장자가 없으면 기본값 'jpg'
}

function generateFilenameWithUuid(url: string, type?: string) {
  // v4는 랜덤한 UUID를 생성합니다.
  const uuid = uuidv4();
  const extension = type || getFileExtensionFromUrl(url);
  return `${uuid}.${extension}`;
}

export async function createFileFromUrl(
  url: string,
  mimeType?: string,
  filename?: string,
): Promise<File | null> {
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'no-cache' });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    mimeType = mimeType ?? getMimeTypeFromUrl(url);
    filename = filename ?? generateFilenameWithUuid(url, mimeType.includes('model') ? 'glb' : ''); // UUID로 동적 파일 이름 생성

    const file = new File([blob], filename, { type: mimeType });

    return file;
  } catch (error) {
    console.error('Error creating file from URL:', error);
    return null;
  }
}

export async function fileToBase64(file: File): Promise<{ mediaType: string; data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const [header, data] = result.split(',');
      const mediaType = header.match(/:(.*?);/)?.[1] ?? file.type;
      resolve({ mediaType, data });
    };
    reader.onerror = (error) => reject(error);
  });
}

export function downloadFileFromUrl(url: string, filename?: string) {
  createFileFromUrl(url).then((file) => {
    if (file) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(file);
      a.download = filename ?? file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.error('Failed to create file from URL');
    }
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export function remove<T>(array: T[], value: T) {
  const index = array.indexOf(value);
  if (index !== -1) {
    array.splice(index, 1);
  }
}

/**
 * 주어진 이미지 파일의 크기를 100x100 픽셀로 조절하고
 * JPEG 포맷으로 변환하여 새로운 File 객체를 반환하는 함수.
 * @param {File} file - 입력 이미지 파일
 * @returns {Promise<File>} - 리사이즈된 이미지의 File 객체
 */
export function resizeImageAsFile(file: File): Promise<File> {
  const fileName = file.name;
  return new Promise<File>((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('유효한 이미지 파일이 아닙니다.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const img = new Image();

      // S3 등 cross-origin 이미지는 CORS 설정 필수
      const imgSrc = event.target?.result as string;
      const isCrossOrigin = !imgSrc.startsWith('blob:') && !imgSrc.startsWith('data:');
      if (isCrossOrigin) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 130;
        const MAX_HEIGHT = 130;

        canvas.width = MAX_WIDTH;
        canvas.height = MAX_HEIGHT;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('캔버스 컨텍스트를 가져올 수 없습니다.'));
          return;
        }

        // 이미지와 캔버스의 비율 계산
        const imgRatio = img.width / img.height;
        const canvasRatio = MAX_WIDTH / MAX_HEIGHT;

        let sx = 0,
          sy = 0,
          sw = img.width,
          sh = img.height;

        if (imgRatio > canvasRatio) {
          // 이미지가 더 넓음 - 좌우를 자름
          sw = img.height * canvasRatio;
          sx = (img.width - sw) / 2;
        } else {
          // 이미지가 더 높음 - 상하를 자름
          sh = img.width / canvasRatio;
          sy = (img.height - sh) / 2;
        }

        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, MAX_WIDTH, MAX_HEIGHT);

        // 캔버스 내용을 Blob 객체로 변환
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Blob 변환에 실패했습니다.'));
              return;
            }

            // Blob을 File 객체로 변환
            const resizedFile = new File([blob], fileName, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });

            resolve(resizedFile);
          },
          'image/jpeg',
          0.7,
        ); // JPEG 포맷과 품질 지정
      };

      img.onerror = () => {
        reject(new Error('이미지 로드 중 오류가 발생했습니다.'));
      };

      img.src = imgSrc;
    };

    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };

    reader.readAsDataURL(file);
  });
}

export async function urlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url, { mode: 'cors', cache: 'no-cache' });
  const blob = await response.blob();

  // blob의 타입이 제대로 설정되지 않은 경우 URL에서 추정
  let contentType = blob.type || 'image/png'; // 기본값을 image/png로 설정

  // Content-Type 헤더에서 확인
  const responseContentType = response.headers.get('Content-Type');
  if (responseContentType && responseContentType.startsWith('image/')) {
    contentType = responseContentType;
  } else if (!blob.type || blob.type === 'text/plain' || blob.type === 'application/octet-stream') {
    // 잘못된 MIME 타입인 경우 URL에서 추정
    if (url.includes('.png')) {
      contentType = 'image/png';
    } else if (url.includes('.jpg') || url.includes('.jpeg')) {
      contentType = 'image/jpeg';
    } else if (url.includes('.gif')) {
      contentType = 'image/gif';
    } else if (url.includes('.webp')) {
      contentType = 'image/webp';
    } else {
      // PNG 바이너리 시그니처 확인
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // PNG 시그니처: 89 50 4E 47 0D 0A 1A 0A
      if (
        uint8Array.length >= 8 &&
        uint8Array[0] === 0x89 &&
        uint8Array[1] === 0x50 &&
        uint8Array[2] === 0x4e &&
        uint8Array[3] === 0x47
      ) {
        contentType = 'image/png';
      }
      // JPEG 시그니처: FF D8 FF
      else if (
        uint8Array.length >= 3 &&
        uint8Array[0] === 0xff &&
        uint8Array[1] === 0xd8 &&
        uint8Array[2] === 0xff
      ) {
        contentType = 'image/jpeg';
      }
      // GIF 시그니처: 47 49 46 38
      else if (
        uint8Array.length >= 4 &&
        uint8Array[0] === 0x47 &&
        uint8Array[1] === 0x49 &&
        uint8Array[2] === 0x46 &&
        uint8Array[3] === 0x38
      ) {
        contentType = 'image/gif';
      }
    }
  }

  // 올바른 MIME 타입으로 새로운 blob 생성
  const correctedBlob = new Blob([blob], { type: contentType });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      // console.log(`🔧 Converted URL to data URL: ${url} -> ${contentType}`);
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(correctedBlob);
  });
}

/**
 * GLB 파일 URL에서 썸네일 이미지를 생성합니다.
 * Three.js를 사용하여 GLB 모델을 렌더링하고 스크린샷을 캡처합니다.
 * @param {string} glbUrl - GLB 파일의 URL
 * @returns {Promise<File>} - 썸네일 이미지의 File 객체
 */
export async function createGLBThumbnail(glbUrl: string): Promise<File> {
  // Dynamic import to avoid SSR issues
  const THREE = await import('three');
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');

  return new Promise((resolve, reject) => {
    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);

    // Create camera
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);

    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(512, 512);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    scene.add(directionalLight);

    // Add another light from opposite direction
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-5, 5, -5);
    scene.add(directionalLight2);

    // Load GLB model
    const loader = new GLTFLoader();
    loader.load(
      glbUrl,
      (gltf) => {
        const model = gltf.scene;

        // Center and scale the model
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        model.scale.multiplyScalar(scale);

        // Center the model
        box.setFromObject(model);
        box.getCenter(center);
        model.position.sub(center);

        // Add model to scene
        scene.add(model);

        // Position camera
        const distance = 3;
        camera.position.set(distance, distance * 0.8, distance);
        camera.lookAt(0, 0, 0);

        // Render the scene
        renderer.render(scene, camera);

        // Convert canvas to blob
        renderer.domElement.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'glb-thumbnail.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              // Clean up
              renderer.dispose();
              resolve(file);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/jpeg',
          0.9,
        );
      },
      undefined,
      (error) => {
        renderer.dispose();
        reject(
          new Error(
            `Failed to load GLB file: ${error instanceof Error ? error.message : String(error)}`,
          ),
        );
      },
    );
  });
}
/**
 * Data URL을 File 객체로 변환합니다.
 * @param dataurl 변환할 Data URL 문자열
 * @param filename 생성할 File 객체의 파일 이름
 * @returns 변환된 File 객체
 */
export const dataURLtoFile = (dataurl: string, filename: string): File => {
  // Data URL에서 MIME 타입과 Base64로 인코딩된 데이터를 분리합니다.
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);

  if (!mimeMatch) {
    throw new Error('Invalid Data URL format: MIME type not found.');
  }

  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  // 이진 데이터를 Uint8Array로 변환합니다.
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // File 객체를 생성하여 반환합니다.
  return new File([u8arr], filename, { type: mime });
};

/**
 * Error 객체나 문자열을 안전하게 문자열로 변환하는 유틸 함수
 * @param error Error 객체 또는 문자열
 * @returns 에러 메시지 문자열
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * 에러를 안전하게 toast로 표시하는 유틸 함수
 * @param error Error 객체 또는 문자열
 * @param prefix 에러 메시지 앞에 붙일 접두사 (선택사항)
 */
export function showErrorToast(error: unknown, prefix?: string): void {
  const errorMessage = getErrorMessage(error);
  const message = prefix ? `${prefix}: ${errorMessage}` : errorMessage;
  toast.error(message);
}

export async function getVerifiedToken() {
  const BASE = process.env.NEXT_PUBLIC_API_SERVER;
  const STORAGE_KEY_ACCESS_TOKEN = 'api:access_token';
  const STORAGE_KEY_REFRESH_TOKEN = 'api:refresh_token';

  const setRefreshToken = async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
    if (refreshToken) {
      const formData = new FormData();
      formData.append('refresh_token', refreshToken);
      const refreshRes = await fetch(`${BASE}/api/auth/refresh/`, {
        method: 'POST',
        body: formData,
      });
      if (refreshRes.status === 200) {
        const jsonRes = await refreshRes.json();
        localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, jsonRes.access_token);
        localStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, jsonRes.refresh_token);
        return jsonRes.access_token;
      } else if (refreshRes.status === 401) {
        localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
        return undefined;
      } else {
        console.log('refresh else case', refreshRes);
        console.log('refresh else case', await refreshRes.json());
        return undefined;
      }
    } else {
      localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
      return undefined;
    }
  };

  const token = localStorage.getItem('api:access_token')!;
  let newToken = token;
  try {
    const payload = token.split('.')[1];
    // Base64 디코딩
    const decodedPayload = JSON.parse(atob(payload));
    // exp는 초 단위 Unix timestamp
    const expirationTime = decodedPayload.exp * 1000; // 밀리초로 변환
    const currentTime = Date.now();

    const expired = currentTime >= expirationTime;
    // console.log('TOKEN EXPIRED : ', expired);
    if (expired) {
      newToken = await setRefreshToken();
    }
  } catch (error) {
    //파싱 실패시도 새로운 토큰 발급
    newToken = await setRefreshToken();
  }
  return newToken;
}

/**
 * 이미지 비율이 2를 초과하면 2:1 비율로 crop
 * @param {string} imageUrl - 이미지 URL
 * @returns {Promise<string>} - crop된 이미지의 data URL
 */
export async function cropExceedImageSize(
  imageUrl: string,
  ratioLimit: number = 2,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // CORS 처리
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const ratio = Math.max(width, height) / Math.min(width, height);

      // 비율이 2 이하면 원본 그대로 반환
      if (ratio <= ratioLimit) {
        resolve(imageUrl);
        return;
      }

      // 비율이 2를 초과하면 crop
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let cropWidth, cropHeight, offsetX, offsetY;

      if (width > height) {
        // 가로가 긴 경우
        cropWidth = height * 2; // 2:1 비율
        cropHeight = height;
        offsetX = (width - cropWidth) / 2; // 중앙에서 crop
        offsetY = 0;
      } else {
        // 세로가 긴 경우
        cropWidth = width;
        cropHeight = width * 2; // 1:2 비율
        offsetX = 0;
        offsetY = (height - cropHeight) / 2; // 중앙에서 crop
      }

      canvas.width = cropWidth;
      canvas.height = cropHeight;
      // 이미지를 canvas에 그리기 (crop된 부분만)
      ctx!.drawImage(
        img,
        offsetX,
        offsetY,
        cropWidth,
        cropHeight, // 소스 crop 영역
        0,
        0,
        cropWidth,
        cropHeight, // 캔버스에 그릴 영역
      );

      // canvas를 data URL로 변환
      const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
      resolve(croppedImageUrl);
    };

    img.onerror = reject;
    img.src = imageUrl;
  });
}

/**
 * 이미지 비율이 2를 초과하면 2:1 비율로 crop
 * @param {string} imageUrl - 이미지 URL
 * @returns {Promise<string>} - crop된 이미지의 data URL
 */
export async function isImageRatioExceeded(
  imageUrl: string,
  ratioLimit: number = 2,
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // CORS 처리
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const ratio = Math.max(width, height) / Math.min(width, height);

      // 비율이 2 이하면 원본 그대로 반환
      if (ratio <= ratioLimit) {
        resolve(false);
        return;
      } else {
        resolve(true);
        return;
      }
    };

    img.onerror = reject;
    img.src = imageUrl;
  });
}

/**
 * 이미지를 1920px 이하로 리사이징 (비율 유지)
 * @param file 원본 이미지 파일
 * @param maxSize 최대 크기 (기본: 1920)
 * @param quality JPEG 압축 품질 (0-1, 기본: 0.9)
 * @returns 리사이징된 이미지 파일
 */
async function resizeImageIfNeeded(
  file: File,
  maxSize: number = 1920,
  minSize: number = 256,
  quality: number = 0.9,
): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      console.log('📄 Not an image file, returning original');
      resolve(file);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const { width, height } = img;

        let needsResize = false;
        let newWidth = width;
        let newHeight = height;
        let operation = 'none';

        // 너무 작은 경우: 업스케일
        if (width < minSize || height < minSize) {
          const scale = Math.max(minSize / width, minSize / height);
          newWidth = Math.ceil(width * scale);
          newHeight = Math.ceil(height * scale);
          needsResize = true;
          operation = 'upscale';
          console.log('⬆️ Upscaling required');
        }
        // 너무 큰 경우: 다운스케일
        else if (width > maxSize || height > maxSize) {
          const scale = Math.min(maxSize / width, maxSize / height);
          newWidth = Math.floor(width * scale);
          newHeight = Math.floor(height * scale);
          needsResize = true;
          operation = 'downscale';
          console.log('⬇️ Downscaling required');
        }

        // 리사이징 불필요
        if (!needsResize) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }

        console.log(`🔄 ${operation === 'upscale' ? 'Upscaling' : 'Downscaling'} image...`, {
          newResolution: `${newWidth}x${newHeight}`,
          scale: `${((newWidth / width) * 100).toFixed(1)}%`,
        });

        // Canvas 생성 및 리사이징
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas context not available');
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            let mimeType: string;
            let extension: string;

            if (file.type === 'image/png') {
              mimeType = 'image/png';
              extension = '.png';
            } else if (file.type === 'image/webp') {
              mimeType = 'image/webp';
              extension = '.webp';
            } else if (file.type === 'image/gif') {
              mimeType = 'image/gif';
              extension = '.gif';
            } else {
              // 기본값은 JPEG (jpg, jpeg, 기타)
              mimeType = 'image/jpeg';
              extension = '.jpg';
            }

            //(원본 확장자 유지)
            const originalExtension = file.name.match(/\.[^.]+$/)?.[0] || extension;
            const fileName = file.name.replace(/\.[^.]+$/, originalExtension);

            const resizedFile = new File([blob], fileName, {
              type: mimeType,
              lastModified: Date.now(),
            });

            // console.log(fileName, originalExtension, mimeType);
            const sizeDiff = resizedFile.size - file.size;
            const sizeChangePercent = ((sizeDiff / file.size) * 100).toFixed(1);

            console.log('📊Resize Summary:', {
              operation,
              resolutionChange: `${width}x${height} → ${newWidth}x${newHeight}`,
              sizeChange: `${sizeChangePercent}% ${sizeDiff > 0 ? 'larger' : 'smaller'}`,
            });

            URL.revokeObjectURL(objectUrl);
            resolve(resizedFile);
          },
          'image/jpeg',
          quality,
        );
      } catch (error) {
        console.error('❌ Resize error:', error);
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      console.error('❌ Failed to load image');
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
}

/**
 * 여러 이미지 파일 일괄 리사이징
 */
export async function resizeMultipleImages(
  files: File[],
  maxSize: number = 1920,
  minSize: number = 256,
  quality: number = 0.9,
): Promise<File[]> {
  const resizePromises = files.map((file) => resizeImageIfNeeded(file, maxSize, minSize, quality));
  return Promise.all(resizePromises);
}

export async function cropToAspectRatio(
  imageUrl: string,
  targetRatio: number, // 목표 비율 (가로/세로)
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const { width, height } = img;
      const currentRatio = width / height;

      let srcX: number, srcY: number, srcWidth: number, srcHeight: number;

      if (currentRatio > targetRatio) {
        // 현재가 더 가로로 길면 → 좌우 자르기
        srcHeight = height;
        srcWidth = height * targetRatio;
        srcX = (width - srcWidth) / 2;
        srcY = 0;
      } else {
        // 현재가 더 세로로 길면 → 위아래 자르기
        srcWidth = width;
        srcHeight = width / targetRatio;
        srcX = 0;
        srcY = (height - srcHeight) / 2;
      }

      // 정수로 반올림하여 렌더링 이슈 방지
      srcX = Math.round(srcX);
      srcY = Math.round(srcY);
      srcWidth = Math.round(srcWidth);
      srcHeight = Math.round(srcHeight);

      const canvas = document.createElement('canvas');
      canvas.width = srcWidth;
      canvas.height = srcHeight;
      const ctx = canvas.getContext('2d')!;

      // 배경을 흰색으로 채워 투명 영역 방지
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 이미지 그리기
      ctx.drawImage(img, srcX, srcY, srcWidth, srcHeight, 0, 0, srcWidth, srcHeight);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
}
